import React, { Suspense, useEffect } from 'react';

import { useLittleSpoonInventory } from './littleSpoonInventoryQueries';

const loadReport = () => import('./LittleSpoonInventoryPlanning');
const LittleSpoonInventoryPlanning = React.lazy(loadReport);

const PAGE_STYLE = { padding: 24 };
const ERROR_STYLE = { padding: 24, maxWidth: 640 };
const loadingMessage = <div style={PAGE_STYLE}>Loading Little Spoon inventory…</div>;
const forbiddenMessage = <div style={PAGE_STYLE}>You do not have access to the Little Spoon inventory report.</div>;

const retryButtonStyle = (busy) => ({
    marginTop: 16,
    padding: '6px 14px',
    fontSize: 13,
    cursor: busy ? 'default' : 'pointer',
    border: '1px solid #232F36',
    background: busy ? '#6E7C84' : '#232F36',
    color: '#FFFFFF',
    borderRadius: 2,
});

/**
 * Authenticated wrapper for the Little Spoon inventory and cover report.
 *
 * There is deliberately no bundled-extract fallback. This report is shared with
 * Little Spoon, and an old extract that looks live is worse than an explicit
 * outage — the same call made for the Blue Apron Perfect Order Report.
 */
export function LittleSpoonInventoryPlanningPage() {
    const { data, error, isFetching, refetch, status } = useLittleSpoonInventory();

    // Fetch the report chunk in parallel with the data rather than after it.
    useEffect(() => {
        loadReport();
    }, []);

    if (status === 'pending') {
        return loadingMessage;
    }

    if (status === 'error' && error?.forbidden) {
        return forbiddenMessage;
    }

    if (status === 'error') {
        return (
            <div style={ERROR_STYLE}>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Little Spoon inventory is unavailable</h1>
                <p style={{ margin: '8px 0 0', fontSize: 14, color: '#3C474D', lineHeight: 1.6 }}>
                    We couldn’t load live data for this report. This usually means the report service is temporarily
                    unavailable — please try again in a moment.
                </p>
                {error?.message ? (
                    <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#6E7C84' }}>Details: {error.message}</p>
                ) : null}
                <button
                    type="button"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    style={retryButtonStyle(isFetching)}
                >
                    {isFetching ? 'Retrying…' : 'Retry'}
                </button>
            </div>
        );
    }

    return (
        <Suspense fallback={loadingMessage}>
            <LittleSpoonInventoryPlanning data={data} />
        </Suspense>
    );
}
